import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, enterStudentPreview } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import logger from "../lib/logger";
import { Plus, Users, ArrowRight, Trash2, X, Share2, Copy, Check, Eye, EyeOff, Send, FileEdit } from "lucide-react";

const COLORS = ["yellow", "blue", "mint", "pink"];
const COPY_FEEDBACK_MS = 1800;
const QR_SIZE = 240;
const COURSE_CAPACITY = 50;

function enrollButtonLabel(course, enrollingId) {
  if (course.student_count >= COURSE_CAPACITY) return "Course full";
  if (enrollingId === course.id) return "Enrolling...";
  return "Enroll now";
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", code: "", color: "yellow", is_published: false });
  const [error, setError] = useState("");
  const [enrollingId, setEnrollingId] = useState(null);
  const [shareCourse, setShareCourse] = useState(null);
  const [copied, setCopied] = useState(false);

  const isInstructor = user?.role === "instructor";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/courses");
      setCourses(res.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCourse = async (e) => {
    e.preventDefault();
    setCreating(true); setError("");
    try {
      await api.post("/courses", form);
      setShowNew(false);
      setForm({ title: "", description: "", code: "", color: "yellow", is_published: false });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setCreating(false); }
  };

  const enroll = async (cid) => {
    setEnrollingId(cid);
    try {
      await api.post(`/courses/${cid}/enroll`);
      load();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setEnrollingId(null); }
  };

  const removeCourse = async (cid) => {
    if (!window.confirm("Delete this course? All materials, exams, projects, and chat will be removed.")) return;
    try {
      await api.delete(`/courses/${cid}`);
      load();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const enrollUrl = (c) => `${window.location.origin}/register?course=${encodeURIComponent(c.code || "")}`;
  const copyLink = async (c) => {
    try {
      await navigator.clipboard.writeText(enrollUrl(c));
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    } catch (err) {
      logger.warn("Clipboard write failed:", err);
    }
  };

  const togglePublic = async (c) => {
    try {
      await api.patch(`/courses/${c.id}`, { is_public: !c.is_public });
      setCourses((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_public: !c.is_public } : x)));
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const togglePublished = async (c) => {
    const nextPublished = !(c.is_published !== false);
    try {
      await api.patch(`/courses/${c.id}`, { is_published: nextPublished });
      setCourses((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_published: nextPublished } : x)));
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <header className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6 animate-pop">
          <div>
            <div className="tag tag-pink mb-3 inline-block">Spring 2026</div>
            <h1 className="font-display text-5xl md:text-6xl font-bold leading-[0.95]">
              Hi {user?.name?.split(" ")[0]}, <br />
              <span className="bg-[var(--gold)] text-[var(--navy-on-gold)] px-3 inline-block border-2 border-[var(--gold)] mt-2">your courses</span>
            </h1>
            <p className="mt-4 text-[var(--muted-text)] max-w-lg">
              {isInstructor
                ? "Manage your courses for Central Florida Corporate Solutions. Post materials, create exams, review submissions and chat with your class."
                : "Browse courses below. Enroll in any course to access materials, projects, exams and the class chatroom."}
            </p>
          </div>
          {isInstructor && (
            <div className="flex flex-wrap gap-2 self-start">
              <button data-testid="view-as-student-btn" onClick={enterStudentPreview} className="nb-btn nb-btn-white flex items-center gap-2" title="Preview the dashboard and courses exactly as a student sees them">
                <Eye className="w-4 h-4" /> View as student
              </button>
              <button data-testid="new-course-btn" onClick={() => setShowNew(true)} className="nb-btn nb-btn-yellow flex items-center gap-2">
                <Plus className="w-4 h-4" /> New course
              </button>
            </div>
          )}
        </header>

        {loading && <p>Loading courses...</p>}
        {!loading && courses.length === 0 && (
          <div className="nb-card-static p-10 text-center max-w-xl">
            <h2 className="font-display text-2xl font-bold mb-2">No courses yet</h2>
            <p className="text-[var(--muted-text)] text-sm">
              {isInstructor ? "Click \"New course\" to publish your first course." : "Your instructor hasn't published any courses. Check back soon."}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {courses.map((c, i) => {
            const headerCls = "bg-[var(--cream)] text-[var(--navy-on-gold)]";
            return (
              <article key={c.id} data-testid={`course-card-${c.id}`} className="nb-card p-6 flex flex-col animate-pop relative" style={{ animationDelay: `${i * 60}ms` }}>
                {isInstructor && c.is_published === false && (
                  <span data-testid={`draft-badge-${c.id}`} className="absolute top-3 right-3 z-10 px-2 py-1 rounded-md bg-[var(--muted-text)]/20 border border-[var(--muted-text)] text-[10px] font-bold uppercase tracking-wider text-[var(--gold-soft)] flex items-center gap-1">
                    <FileEdit className="w-3 h-3" /> Draft
                  </span>
                )}
                <div className={`${headerCls} -mx-6 -mt-6 px-6 py-5 border-b-2 border-[var(--gold)] flex items-center justify-between`}>
                  <span className="font-mono text-sm font-bold uppercase tracking-wider text-[var(--navy-on-gold)]">{c.code}</span>
                  <div className="flex items-center gap-1.5 text-sm font-bold">
                    <Users className="w-4 h-4" /> {c.student_count}/50
                  </div>
                </div>
                <h2 className="font-display text-3xl font-bold mt-5 leading-tight text-[var(--cream)]">{c.title}</h2>
                <p className="text-base text-[var(--gold-soft)] mt-3 line-clamp-3 flex-1">{c.description}</p>
                <p className="text-sm mt-4 text-[var(--gold)] font-semibold">By Dr. Ramzy Baroody</p>

                <div className="mt-5 flex gap-2">
                  {isInstructor ? (
                    <>
                      <button data-testid={`open-course-${c.id}`} onClick={() => navigate(`/courses/${c.id}`)} className="nb-btn nb-btn-white text-sm flex-1 flex items-center justify-center gap-2">
                        Manage <ArrowRight className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`toggle-published-${c.id}`}
                        onClick={() => togglePublished(c)}
                        className={`nb-btn text-sm ${c.is_published !== false ? "nb-btn-yellow" : "nb-btn-white"}`}
                        title={c.is_published !== false ? "Published — click to move back to draft" : "Draft — click to publish to students"}
                      >
                        {c.is_published !== false ? <Send className="w-4 h-4" /> : <FileEdit className="w-4 h-4" />}
                      </button>
                      <button data-testid={`toggle-public-${c.id}`} onClick={() => togglePublic(c)} className={`nb-btn text-sm ${c.is_public ? "nb-btn-mint" : "nb-btn-white"}`} title={c.is_public ? "Hide from landing page" : "Show on landing page"}>
                        {c.is_public ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button data-testid={`share-course-${c.id}`} onClick={() => { setShareCourse(c); setCopied(false); }} className="nb-btn nb-btn-white text-sm" title="Share enrollment link">
                        <Share2 className="w-4 h-4" />
                      </button>
                      <button data-testid={`delete-course-${c.id}`} onClick={() => removeCourse(c.id)} className="nb-btn nb-btn-white text-sm" title="Delete course">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  ) : c.enrolled ? (
                    <button data-testid={`enter-course-${c.id}`} onClick={() => navigate(`/courses/${c.id}`)} className="nb-btn nb-btn-mint text-sm flex-1 flex items-center justify-center gap-2">
                      Enter classroom <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button data-testid={`enroll-course-${c.id}`} disabled={enrollingId === c.id || c.student_count >= COURSE_CAPACITY} onClick={() => enroll(c.id)} className="nb-btn nb-btn-yellow text-sm flex-1">
                      {enrollButtonLabel(c, enrollingId)}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </main>

      {showNew && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={() => setShowNew(false)}>
          <div className="nb-card-static p-7 w-full max-w-lg animate-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-bold">New course</h2>
              <button onClick={() => setShowNew(false)} className="w-9 h-9 border-2 border-[var(--gold)] rounded-lg flex items-center justify-center hover:bg-[var(--yellow)]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={createCourse} className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Course code</label>
                <input data-testid="new-course-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="nb-input" placeholder="CS-101" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Title</label>
                <input data-testid="new-course-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="nb-input" placeholder="Intro to Algorithms" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Description</label>
                <textarea data-testid="new-course-description" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="nb-input min-h-[100px]" placeholder="What students will learn..." />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-2 block">Color</label>
                <div className="flex gap-2">
                  {COLORS.map(co => (
                    <button data-testid={`new-course-color-${co}`} type="button" key={co} onClick={() => setForm({ ...form, color: co })} className={`w-10 h-10 border-2 border-[var(--gold)] rounded-lg ${form.color === co ? "ring-2 ring-offset-2 ring-black" : ""}`} style={{ background: `var(--${co})` }} />
                  ))}
                </div>
              </div>
              <label className="flex items-start gap-3 p-4 border-2 border-[var(--gold)] rounded-lg cursor-pointer hover:bg-[var(--paper-soft)] transition-colors">
                <input
                  data-testid="new-course-publish"
                  type="checkbox"
                  checked={form.is_published}
                  onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-[var(--gold)] shrink-0"
                />
                <div className="text-sm">
                  <div className="font-semibold text-[var(--gold)]">
                    {form.is_published ? "Publish immediately" : "Save as draft"}
                  </div>
                  <div className="text-xs text-[var(--muted-text)] mt-0.5">
                    {form.is_published
                      ? "Students will see this course on their dashboard right away."
                      : "Only you can see this course until you flip the toggle to publish."}
                  </div>
                </div>
              </label>
              {error && <div className="text-sm text-red-600">{error}</div>}
              <button data-testid="new-course-submit" disabled={creating} type="submit" className="nb-btn nb-btn-yellow w-full">
                {creating ? "Creating..." : "Create course"}
              </button>
            </form>
          </div>
        </div>
      )}

      {shareCourse && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={() => setShareCourse(null)}>
          <div data-testid="share-modal" className="nb-card-static p-7 w-full max-w-md animate-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-bold text-[var(--gold)]">Share enrollment</h2>
              <button onClick={() => setShareCourse(null)} className="w-9 h-9 border-2 border-[var(--gold)] rounded-lg flex items-center justify-center hover:bg-[var(--yellow)]"><X className="w-4 h-4" /></button>
            </div>
            <div className="mb-4">
              <p className="text-xs uppercase tracking-wider text-[var(--gold-soft)]">{shareCourse.code}</p>
              <p className="font-display text-xl font-bold text-[var(--cream)] mt-1">{shareCourse.title}</p>
            </div>
            <div className="flex justify-center my-5">
              <img
                data-testid="share-qr"
                alt="enrollment QR"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=${QR_SIZE}x${QR_SIZE}&margin=8&data=${encodeURIComponent(enrollUrl(shareCourse))}`}
                className="border-2 border-[var(--gold)] rounded-lg bg-white p-2"
                width={QR_SIZE}
                height={QR_SIZE}
              />
            </div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1 block text-[var(--gold-soft)]">Direct link</label>
            <div className="flex gap-2">
              <input data-testid="share-link-input" readOnly value={enrollUrl(shareCourse)} className="nb-input flex-1 text-xs" />
              <button data-testid="copy-share-link" onClick={() => copyLink(shareCourse)} className="nb-btn nb-btn-yellow text-sm flex items-center gap-1.5">
                {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
              </button>
            </div>
            <p className="mt-4 text-xs text-[var(--muted-text)] leading-relaxed">
              Print or text this QR code. Students scan, register, then enroll in <strong className="text-[var(--gold)]">{shareCourse.code}</strong> from their dashboard.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
